import {
  Divider,
  IconButton,
  Stack,
  styled,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { ReactComponent as MessagesIcon } from "images/messages.svg";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "store/hooks";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { TextFieldWithEllipsis } from "UI/TextFiledWithEllipsis";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { customColors } from "models/customColors";

const MessageIndicatorImage = styled(MessagesIcon)`
  & path {
    fill: #71839b;
  }
`;

const RedNumber = styled(Stack)`
  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;
  background-color: #ff0000;
  color: white;
  border-radius: 50%;
  font-size: 10px;
`;

const NewMessagesNumber = styled(RedNumber)`
  position: absolute;
  bottom: -4px;
  right: -4px;

  width: 20px;
  height: 20px;
  font-size: 12px;
`;

const ReferralList = styled(Stack)`
  position: absolute;
  padding: 16px 26px 20px 20px;
  top: calc(100% + 5px);
  z-index: 2000;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
  min-width: 348px;
  min-height: 100px;
  max-height: 500px;
  overflow-y: auto;
  font-size: 12px;

  gap: 0;
`;

export const MessageIndicator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { createApiCall } = useCreateApiCall();
  const newMessages = useAppSelector((state) => state.newMessages);
  const [isListVisible, setIsListVisible] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  const clearSearch = () => setSearch("");

  const loadData = createApiCall(
    async () => {
      const result = await API.post("restApiV2", "/messages/new/by-referral", {
        body: {
          dashboardAccessToken: null,
        },
      });

      if (result?.error) {
        console.error(result.error);
        return;
      }

      const newData = result ?? [];
      newData.sort((a, b) => b.practiceName.localeCompare(a.practiceName));

      setData(
        newData?.filter(({ numberOfNewMessages }) => numberOfNewMessages > 0)
      );
    },
    (error) => {
      console.error(error);
      setData([]);
    },
    true
  );

  const openReferral = (id) => {
    navigate(`/referral/${id}?c=messages`);
  };

  useEffect(() => {
    if (!isListVisible) {
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListVisible, newMessages]);

  const searchResult = data?.filter((item) => {
    return (
      item.practiceName.toLowerCase().includes(search.toLowerCase()) ||
      item.originatorName.toLowerCase().includes(search.toLowerCase())
    );
  });

  const summ = data?.reduce((acc, item) => acc + item.numberOfNewMessages, 0);

  return newMessages > 0 ? (
    <div
      className="relative cursor-pointer select-none"
      onClick={() => setIsListVisible((prev) => !prev)}
    >
      <MessageIndicatorImage />
      {!!newMessages && <NewMessagesNumber>{newMessages}</NewMessagesNumber>}
      {isListVisible && (
        <ReferralList sx={{ right: isMobile ? -110 : -5 }}>
          <Stack
            alignItems={"flex-end"}
            sx={{
              margin: 0,
              position: "absolute",
              top: 3,
              right: 3,
            }}
          >
            <IconButton
              aria-label="close"
              size="small"
              sx={{ backgroundColor: "lightgray" }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {data === null && <div>Loading...</div>}
          {data?.length === 0 && <div>No new messages</div>}
          {data?.length > 0 && (
            <>
              <div className="font-bold text-sm">New Messages ({summ})</div>
              <TextField
                value={search}
                placeholder="Search"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  margin: "10px 0",
                  width: "100%",
                  backgroundColor:
                    search === "" ? undefined : customColors.selector,
                }}
                slotProps={{
                  htmlInput: {
                    style: {
                      fontSize: "12px",
                      padding: "0.5rem",
                      border: "none",
                      width: "100%",
                    },
                    autoComplete: "off",
                  },
                  input: {
                    startAdornment: (
                      <SearchIcon
                        sx={{ color: customColors.grayText, margin: 0 }}
                      />
                    ),
                    endAdornment: !search?.length ? null : (
                      <ClearIcon
                        onClick={clearSearch}
                        sx={{ cursor: "pointer", margin: 0 }}
                      />
                    ),
                  },
                }}
              />
            </>
          )}
          {searchResult?.length === 0 && data?.length > 0 && (
            <div>No search results</div>
          )}
          {searchResult?.map((item) => (
            <React.Fragment key={item.referralId}>
              <div className="hover:bg-gray-100 flex flex-col">
                <div className="pl-2 text-gray-400">{item.type}</div>
                <div
                  className={["flex flex-row justify-start pb-1.5"].join(" ")}
                  onClick={() => openReferral(item.referralId)}
                >
                  <div className="w-20 pl-2">
                    <TextFieldWithEllipsis>
                      {item.referralId.slice(0, 8)}
                    </TextFieldWithEllipsis>
                  </div>
                  <div className="w-16 sm:w-24 text-left">
                    <TextFieldWithEllipsis>
                      {item.practiceName}
                    </TextFieldWithEllipsis>
                  </div>
                  <div className="w-16 sm:w-24 text-left">
                    <TextFieldWithEllipsis>
                      {item.originatorName}
                    </TextFieldWithEllipsis>
                  </div>
                  <div className="w-16 sm:w-24 text-left">
                    <TextFieldWithEllipsis>
                      {item.patientName}
                    </TextFieldWithEllipsis>
                  </div>
                  <div className="w-10 text-left">
                    <RedNumber>{item.numberOfNewMessages}</RedNumber>
                  </div>
                </div>
              </div>
              <Divider />
            </React.Fragment>
          ))}
        </ReferralList>
      )}
    </div>
  ) : null;
};
